/**
 * IMDC Creative Works - API Service
 * 
 * This service handles all API communications between the frontend and backend.
 * Update the API_BASE_URL to match your server configuration.
 */
// Prefer an explicit env var, but fall back to the deployed live backend in production.
const DEFAULT_SERVER_ORIGIN = 'https://imdccwserver-7cdb2015fa07.herokuapp.com';
const DEFAULT_LOCAL_API_BASE_URL = 'http://localhost:5000/api';

const getRuntimeOrigin = (): string => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return DEFAULT_SERVER_ORIGIN;
};

const normalizeApiBaseUrl = (rawUrl?: string): string => {
  const trimmed = (rawUrl || '').trim();
  const withoutTrailingSlash = trimmed.replace(/\/+$/, '');

  if (!withoutTrailingSlash) {
    if (process.env.NODE_ENV === 'development') {
      return DEFAULT_LOCAL_API_BASE_URL;
    }
    return `${DEFAULT_SERVER_ORIGIN}/api`;
  }

  return withoutTrailingSlash.endsWith('/api')
    ? withoutTrailingSlash
    : `${withoutTrailingSlash}/api`;
};

const API_BASE_URL = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);
const SERVER_ORIGIN = API_BASE_URL.startsWith('http://') || API_BASE_URL.startsWith('https://')
  ? API_BASE_URL.replace(/\/api$/, '')
  : getRuntimeOrigin();

const buildRequestUrls = (endpoint: string): string[] => {
  const urls = [`${API_BASE_URL}${endpoint}`];

  if (API_BASE_URL.endsWith('/api')) {
    const originUrl = `${API_BASE_URL.replace(/\/api$/, '')}${endpoint}`;
    if (!urls.includes(originUrl)) {
      urls.push(originUrl);
    }
  }

  return urls;
};

const fetchWithFallback = async (endpoint: string, config: RequestInit): Promise<Response> => {
  const urls = buildRequestUrls(endpoint);
  let lastResponse: Response | null = null;

  for (let index = 0; index < urls.length; index += 1) {
    const response = await fetch(urls[index], config);
    if (response.ok || response.status !== 404 || index === urls.length - 1) {
      return response;
    }

    lastResponse = response;
  }

  if (lastResponse) {
    return lastResponse;
  }

  throw new Error('Unable to reach the API server. Please make sure the backend is running and reachable.');
};

// Token management
const getToken = (): string | null => {
  return localStorage.getItem('imdc_token');
};

const setToken = (token: string): void => {
  localStorage.setItem('imdc_token', token);
};

const removeToken = (): void => {
  localStorage.removeItem('imdc_token');
};

export const resolveServerUrl = (assetPath?: string | null): string => {
  if (!assetPath) return '';
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
    return assetPath;
  }
  if (assetPath.startsWith('/')) {
    return `${SERVER_ORIGIN}${assetPath}`;
  }
  return assetPath;
};

// API Request helper
const apiRequest = async (
  endpoint: string,
  method: string = 'GET',
  data?: any,
  includeAuth: boolean = true
): Promise<any> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const config: RequestInit = {
    method,
    headers,
  };

  if (data && method !== 'GET') {
    config.body = JSON.stringify(data);
  }

  let response: Response;
  try {
    response = await fetchWithFallback(endpoint, config);
  } catch {
    throw new Error('Unable to reach the API server. Please make sure the backend is running and reachable.');
  }
  let result: any = null;

  // Some endpoints (especially DELETE) can return 204 No Content.
  if (response.status !== 204) {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      result = await response.json();
    } else {
      const text = await response.text();
      result = text ? { message: text } : {};
    }
  }

  if (!response.ok) {
    throw new Error(result?.message || 'An error occurred');
  }

  return result ?? { success: true };
};

// File upload helper
const uploadFile = async (
  endpoint: string,
  formData: FormData,
  includeAuth: boolean = true
): Promise<any> => {
  const headers: Record<string, string> = {};

  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  let response: Response;
  try {
    response = await fetchWithFallback(endpoint, {
      method: 'POST',
      headers,
      body: formData,
    });
  } catch {
    throw new Error('Unable to reach the API server. Please make sure the backend is running and reachable.');
  }

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.message || 'An error occurred');
  }

  return result;
};

// ============================================
// AUTH API
// ============================================

export const authAPI = {
  login: async (email: string, password: string) => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const result = await apiRequest('/auth/login', 'POST', { email: normalizedEmail, password }, false);

      const token = result?.token || result?.data?.token;
      const user = result?.user || result?.data?.user;

      if (token) {
        setToken(token);
      }

      return {
        ...result,
        token,
        user,
      };
    } catch (error: any) {
      return { error: true, message: error.message };
    }
  },

  register: async (
    name: string,
    email: string,
    password: string,
    department_id: number | null,
    position: string
  ) => {
    const normalizedEmail = email.trim().toLowerCase();
    const result = await apiRequest(
      '/auth/register',
      'POST',
      { name, email: normalizedEmail, password, department_id, position },
      false
    );
    const token = result?.token || result?.data?.token;
    const user = result?.user || result?.data?.user;
    if (token) {
      setToken(token);
    }
    return {
      ...result,
      token,
      user,
    };
  },

  logout: () => {
    removeToken();
    localStorage.removeItem('imdc_user');
  },

  isAuthenticated: () => {
    return !!getToken();
  },
};

// ============================================
// USER/PROFILE API
// ============================================

export const profileAPI = {
  getProfile: async () => {
    return apiRequest('/profile');
  },

  updateProfile: async (data: {
    name: string;
    email: string;
    department_id?: number;
    position?: string;
    bio?: string;
  }) => {
    return apiRequest('/profile', 'PUT', data);
  },

  changePassword: async (old_password: string, new_password: string) => {
    return apiRequest('/profile/password', 'PUT', { old_password, new_password });
  },

  uploadProfilePicture: async (file: File) => {
    const formData = new FormData();
    formData.append('profile_picture', file);
    return uploadFile('/profile/picture', formData);
  },
};

// ============================================
// SUBMISSIONS API
// ============================================

export const submissionsAPI = {
  // Create new submission
  create: async (
    title: string,
    category_id: number,
    description: string,
    file?: File
  ) => {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('category_id', category_id.toString());
    formData.append('description', description);
    if (file) {
      formData.append('file', file);
    }
    return uploadFile('/submissions', formData);
  },

  // Get my submissions (faculty)
  getMySubmissions: async (
    keyword?: string,
    status?: string,
    page: number = 1,
    limit: number = 20
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (keyword) params.append('keyword', keyword);
    if (status) params.append('status', status);
    return apiRequest(`/submissions/my?${params.toString()}`);
  },

  // Get single submission
  getById: async (id: number) => {
    return apiRequest(`/submissions/${id}`);
  },

  // Update submission
  update: async (id: number, data: { title: string; category_id: number; description: string }) => {
    return apiRequest(`/submissions/${id}`, 'PUT', data);
  },

  // Delete submission
  delete: async (id: number) => {
    return apiRequest(`/submissions/${id}`, 'DELETE');
  },
};

// ============================================
// FACULTY API
// ============================================

export const facultyAPI = {
  // Get faculty dashboard stats
  getStats: async () => {
    return apiRequest('/faculty/stats');
  },
};

// ============================================
// ADMIN API
// ============================================

export const adminAPI = {
  // Get dashboard stats
  getDashboardStats: async () => {
    return apiRequest('/admin/dashboard');
  },

  // Get all submissions
  getAllSubmissions: async (
    keyword?: string,
    status?: string,
    category_id?: number,
    page: number = 1,
    limit: number = 20
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (keyword) params.append('keyword', keyword);
    if (status) params.append('status', status);
    if (category_id) params.append('category_id', category_id.toString());
    return apiRequest(`/admin/submissions?${params.toString()}`);
  },

  // Review submission (approve/reject)
  reviewSubmission: async (id: number, status: 'approved' | 'rejected', feedback?: string) => {
    return apiRequest(`/admin/submissions/${id}/review`, 'PUT', { status, feedback });
  },

  // Delete submission (admin)
  deleteSubmission: async (id: number) => {
    try {
      return await apiRequest(`/admin/submissions/${id}`, 'DELETE');
    } catch (error: any) {
      const message = error?.message || '';

      // Fallback for backends that expose delete at /submissions/:id instead of /admin/submissions/:id.
      if (message.includes('Cannot DELETE /api/admin/submissions/')) {
        return apiRequest(`/submissions/${id}`, 'DELETE');
      }

      throw error;
    }
  },

  // Get all faculty
  getAllFaculty: async (
    keyword?: string,
    department_id?: number,
    page: number = 1,
    limit: number = 20
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (keyword) params.append('keyword', keyword);
    if (department_id) params.append('department_id', department_id.toString());
    return apiRequest(`/admin/faculty?${params.toString()}`);
  },

  // Get faculty by id
  getFacultyById: async (id: number) => {
    return apiRequest(`/admin/faculty/${id}`);
  },

  // Delete faculty
  deleteFaculty: async (id: number) => {
    return apiRequest(`/admin/faculty/${id}`, 'DELETE');
  },
};

// ============================================
// LOOKUP API (Public)
// ============================================

export const lookupAPI = {
  getDepartments: async () => {
    return apiRequest('/departments', 'GET', null, false);
  },

  getCategories: async () => {
    return apiRequest('/categories', 'GET', null, false);
  },

  getPublicStats: async () => {
    return apiRequest('/stats', 'GET', null, false);
  },

  getArchive: async (
    keyword?: string,
    category_id?: number,
    department_id?: number,
    page: number = 1,
    limit: number = 20
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (keyword) params.append('keyword', keyword);
    if (category_id) params.append('category_id', category_id.toString());
    if (department_id) params.append('department_id', department_id.toString());
    return apiRequest(`/archive?${params.toString()}`, 'GET', null, false);
  },
};

// Export all APIs
const api = {
  auth: authAPI,
  profile: profileAPI,
  submissions: submissionsAPI,
  faculty: facultyAPI,
  admin: adminAPI,
  lookup: lookupAPI,
};

export default api;
